import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';
import { SUBSCRIPTION_PLANS } from '../../types/subscription';
import { formatAmount } from '../../utils/StripeTestUtils';

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { school } = useSchool();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [planDetails, setPlanDetails] = useState<any>(null);

  const sessionId = searchParams.get('session_id');
  const schoolId = searchParams.get('school_id');
  const planId = searchParams.get('plan_id');
  const userCount = searchParams.get('user_count');

  useEffect(() => {
    if (!sessionId || !schoolId || !user) {
      setError('Missing payment information');
      setProcessing(false);
      return;
    }

    // Find plan details
    if (planId) {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      setPlanDetails(plan);
    }

    processPaymentSuccess();
  }, [sessionId, schoolId, user, planId]);

  const processPaymentSuccess = async () => {
    try {
      // Check if we have a valid session ID from Stripe
      // The session_id parameter will be {CHECKOUT_SESSION_ID} if Stripe hasn't replaced it
      if (!sessionId || sessionId === '{CHECKOUT_SESSION_ID}') {
        // Payment was successful but we're waiting for webhook to process
        setSubscriptionDetails({
          planId,
          planName: planDetails?.name || planId,
          planPrice: planDetails?.price || 0,
          userCount: userCount ? parseInt(userCount) : undefined,
          webhookPending: true
        });
        
        toast.success('Payment received! Your subscription will be activated shortly.');
        
        // Poll for subscription activation (check every 3 seconds, max 10 times)
        let attempts = 0;
        const checkInterval = setInterval(async () => {
          attempts++;
          
          if (school?.id) {
            const schoolDoc = await getDoc(doc(db, 'schools', school.id));
            const data = schoolDoc.data();
            
            if (data?.subscription?.status === 'active' && data?.subscription?.stripeSubscriptionId) {
              clearInterval(checkInterval);
              setSubscriptionDetails({
                ...data.subscription,
                planName: planDetails?.name || planId,
                planPrice: planDetails?.price || 0,
                webhookProcessed: true
              });
              toast.success('Subscription activated successfully!');
              setProcessing(false);
            } else if (attempts >= 10) {
              clearInterval(checkInterval);
              console.log('Subscription activation pending - webhook may still be processing');
              setProcessing(false);
            }
          }
        }, 3000);
        
      } else {
        // We have a session ID - this means we're in test mode or webhook already processed
        // Just display success based on URL parameters
        setSubscriptionDetails({
          planId,
          sessionId,
          planName: planDetails?.name || planId,
          planPrice: planDetails?.price || 0,
          userCount: userCount ? parseInt(userCount) : undefined
        });
        
        toast.success('Payment successful! Your subscription is now active.');
        setProcessing(false);
      }
    } catch (error) {
      console.error('Error processing payment success:', error);
      setError(error instanceof Error ? error.message : 'Failed to process payment');
      toast.error('Payment completed but there was an error checking your account status. Please refresh the page.');
      setProcessing(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment</h2>
          <p className="text-gray-600">
            Please wait while we confirm your payment and activate your subscription...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/billing')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Billing
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
        <p className="text-gray-600 mb-6">
          Thank you for your payment. Your CampusConnect subscription is now active and ready to use.
        </p>
        
        {planDetails && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <span className="text-lg font-bold text-blue-900">{planDetails.name} Plan</span>
              <span className="text-lg font-bold text-blue-600">
                {planDetails.price === 0 ? 'Free' : formatAmount(planDetails.price * 100)}/month
              </span>
            </div>
            <div className="text-sm text-blue-700">
              <p className="mb-1">✅ All features activated</p>
              <p>🎉 Welcome to CampusConnect {planDetails.name}!</p>
            </div>
          </div>
        )}
        
        {subscriptionDetails && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">Subscription Details:</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Plan:</span> {subscriptionDetails.planName}</p>
              {subscriptionDetails.planPrice > 0 && (
                <div>
                  <p><span className="font-medium">Price:</span> {formatAmount(subscriptionDetails.planPrice * 100)}/user/month</p>
                  {subscriptionDetails.userCount && (
                    <div>
                      <p><span className="font-medium">Users:</span> {subscriptionDetails.userCount}</p>
                      <p><span className="font-medium">Total:</span> {formatAmount(subscriptionDetails.planPrice * subscriptionDetails.userCount * 100)}/month</p>
                    </div>
                  )}
                </div>
              )}
              {subscriptionDetails.webhookPending && (
                <p className="text-yellow-600 font-medium">⏳ Activation in progress...</p>
              )}
              {subscriptionDetails.webhookProcessed && (
                <p className="text-green-600 font-medium">✅ Subscription active!</p>
              )}
              {subscriptionDetails.sessionId && (
                <p><span className="font-medium">Session ID:</span> {subscriptionDetails.sessionId.substring(0, 20)}...</p>
              )}
              {subscriptionDetails.stripeCustomerId && (
                <p><span className="font-medium">Customer ID:</span> {subscriptionDetails.stripeCustomerId}</p>
              )}
              {subscriptionDetails.activatedAt && (
                <p><span className="font-medium">Activated:</span> {new Date(subscriptionDetails.activatedAt).toLocaleString()}</p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleContinue}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Continue to Dashboard
          </button>
          <button
            onClick={() => navigate('/billing')}
            className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            View Billing Details
          </button>
        </div>

        <div className="mt-6 space-y-2">
          <p className="text-xs text-gray-500">
            You should receive a confirmation email shortly. If you have any questions, please contact our support team.
          </p>
          {planDetails && planDetails.features && (
            <div className="bg-gray-50 p-3 rounded-lg text-left">
              <p className="text-xs font-medium text-gray-700 mb-1">Your plan includes:</p>
              <div className="grid grid-cols-1 gap-1">
                {planDetails.features.slice(0, 3).map((feature: string, index: number) => (
                  <p key={index} className="text-xs text-gray-600">• {feature}</p>
                ))}
                {planDetails.features.length > 3 && (
                  <p className="text-xs text-gray-500">+ {planDetails.features.length - 3} more features</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;